/* eslint-disable max-lines -- connection provider intentionally monolithic; split in a follow-up */
/* eslint-disable max-lines-per-function -- DataConnectionProvider composes sync, cache, and WebSocket */
/* eslint-disable react-hooks/exhaustive-deps -- deps minimized to avoid connect/sync feedback loops */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import api from '../services/api/api';
import { useModelSelector } from './ModelSelectorContext';
import { WS_BASE_URL } from '../config';
import type { DataConnectionState, DataMetrics, DataConnectionContextType } from './dataConnectionTypes';

export type { DataConnectionState, DataMetrics };

const DataConnectionContext = createContext<DataConnectionContextType | undefined>(undefined);

// Cache TTL in milliseconds (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

export const DataConnectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { environment, dataSource } = useModelSelector();
  
  const [state, setState] = useState<DataConnectionState>({
    isConnected: false,
    isConnecting: false,
    connectionError: null,
    lastSyncTime: null,
    syncInProgress: false,
    dataVersion: '1.0.0',
    cacheStatus: {
      artifacts: 'missing',
      processes: 'missing',
      states: 'missing',
      transitions: 'missing',
    },
    networkStatus: 'online',
    retryCount: 0,
    maxRetries: 3,
  });

  // Data state
  const [artifacts, setArtifacts] = useState<any[]>([]);
  const [processes, setProcesses] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [transitions, setTransitions] = useState<any[]>([]);
  const [isLoading] = useState(false);
  const [error] = useState<string | null>(null);

  const cacheRef = useRef<Map<string, { data: any; timestamp: number }>>(new Map());
  const wsRef = useRef<WebSocket | null>(null);
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => {
      setState(prev => ({ ...prev, networkStatus: 'online' }));
      if (dataSource === 'live') {
        void reconnect();
      }
    };

    const handleOffline = () => {
      setState(prev => ({ ...prev, networkStatus: 'offline' }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [dataSource]);

  // Reset error state function
  const resetErrorState = useCallback(() => {
    setState(prev => ({
      ...prev,
      connectionError: null,
      retryCount: 0,
      isConnecting: false,
      isConnected: false,
    }));
  }, []);

  const clearSampleModeData = useCallback(() => {
    setArtifacts([]);
    setProcesses([]);
    setStates([]);
    setTransitions([]);
    cacheRef.current.clear();
    setState((prev) => ({
      ...prev,
      isConnected: false,
      isConnecting: false,
      syncInProgress: false,
      connectionError: null,
      lastSyncTime: null,
      /** Allow a fresh connect() when switching back to live after failed retries */
      retryCount: 0,
      cacheStatus: {
        artifacts: 'missing',
        processes: 'missing',
        states: 'missing',
        transitions: 'missing',
      },
    }));
  }, []);

  // Auto-connect for live data; sample mode keeps empty arrays (no mock data)
  useEffect(() => {
    if (dataSource === 'sample') {
      disconnect();
      clearSampleModeData();
      return;
    }
    if (dataSource === 'live' && !state.isConnected && !state.isConnecting && state.retryCount < state.maxRetries) {
      resetErrorState();
      void connect();
    }
  }, [dataSource, environment]);

  // Setup periodic sync for live data
  useEffect(() => {
    if (dataSource === 'live' && state.isConnected) {
      syncIntervalRef.current = setInterval(() => {
        void syncData(false);
      }, 30000); // Sync every 30 seconds
    } else {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
    }

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [dataSource, state.isConnected]);

  const connect = useCallback(async () => {
    if (dataSource !== 'live') return;
    
    setState(prev => ({ ...prev, isConnecting: true, connectionError: null }));
    
    try {
      // Test connection
      await api.get('/health/health');
      
      setState(prev => ({
        ...prev,
        isConnected: true,
        isConnecting: false,
        retryCount: 0,
        connectionError: null,
      }));
      
      // Initial data sync
      await syncData(true);
      
      // Subscribe to real-time updates
      subscribeToUpdates();
      
    } catch (error: any) {
      handleConnectionError(error);
    }
  }, [dataSource]);

  const disconnect = useCallback(() => {
    setState(prev => ({
      ...prev,
      isConnected: false,
      isConnecting: false,
    }));
    
    unsubscribeFromUpdates();
    
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }
  }, []);

  const reconnect = useCallback(async () => {
    if (state.retryCount >= state.maxRetries) {
      setState(prev => ({
        ...prev,
        connectionError: 'Maximum retry attempts reached',
        isConnecting: false,
      }));
      return;
    }
    
    setState(prev => ({ ...prev, retryCount: prev.retryCount + 1 }));
    await connect();
  }, [state.retryCount, state.maxRetries, connect]);

  const syncData = useCallback(async (_force = false) => {
    if (dataSource !== 'live' || state.syncInProgress) return;
    
    setState(prev => ({ ...prev, syncInProgress: true }));
    
    try {
      await Promise.all([
        syncArtifacts(),
        syncProcesses(),
        syncStates(),
        syncTransitions(),
      ]);
      
      setState(prev => ({
        ...prev,
        syncInProgress: false,
        lastSyncTime: new Date().toISOString(),
        dataVersion: `${Date.now()}`,
      }));
      
    } catch (error: any) {
      setState(prev => ({ ...prev, syncInProgress: false }));
      handleConnectionError(error);
    }
  }, [dataSource, state.syncInProgress]);

  // Refresh data when environment changes (removed verbose log)
  useEffect(() => {
    if (state.isConnected && dataSource === 'live') {
      void syncData(true); // Force refresh
    }
  }, [environment, state.isConnected, dataSource, syncData]);

  const syncArtifacts = useCallback(async () => {
    const cacheKey = `artifacts_${environment}`;
    const cached = cacheRef.current.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setState(prev => ({
        ...prev,
        cacheStatus: { ...prev.cacheStatus, artifacts: 'fresh' },
      }));
      setArtifacts(cached.data);
      return cached.data;
    }
    
    // Map environment to correct endpoint
    let endpoint: string;
    if (environment.toLowerCase() === 'prime') {
      endpoint = '/prime-artifacts';
    } else {
      endpoint = '/artifacts';
    }
    
    try {
      const response = await api.get(endpoint);
      const data = response.data;
      
      cacheRef.current.set(cacheKey, { data, timestamp: Date.now() });
      setState(prev => ({
        ...prev,
        cacheStatus: { ...prev.cacheStatus, artifacts: 'fresh' },
      }));
      setArtifacts(data);
      
      return data;
    } catch (error: any) {
      console.warn(`❌ [DataConnection] Failed to fetch artifacts from ${endpoint}:`, error.message);
      const emptyData: any[] = [];
      cacheRef.current.set(cacheKey, { data: emptyData, timestamp: Date.now() });
      setState(prev => ({
        ...prev,
        cacheStatus: { ...prev.cacheStatus, artifacts: 'stale' },
      }));
      setArtifacts(emptyData);
      return emptyData;
    }
  }, [environment]);

  const syncProcesses = useCallback(async () => {
    const cacheKey = `processes_${environment}`;
    const cached = cacheRef.current.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setState(prev => ({
        ...prev,
        cacheStatus: { ...prev.cacheStatus, processes: 'fresh' },
      }));
      setProcesses(cached.data);
      return cached.data;
    }
    
    try {
      let data: any[] = [];
      
      if (environment.toLowerCase() === 'prime') {
        // For prime environment, we need to get processes from all prime artifacts
        // First get all prime artifacts
        const artifactsResponse = await api.get('/prime-artifacts');
        const artifacts = artifactsResponse.data;
        
        // Then get processes for each artifact
        const allProcesses: any[] = [];
        for (const artifact of artifacts) {
          try {
            const processesResponse = await api.get(`/prime-artifacts/${artifact.id}/processes`);
            const artifactProcesses = processesResponse.data || [];
            allProcesses.push(...artifactProcesses);
          } catch (error) {
            console.warn(`Failed to fetch processes for artifact ${artifact.id}:`, error);
          }
        }
        
        data = allProcesses;
      } else {
        // For company environment, use the regular processes endpoint
        const endpoint = '/processes';
        const response = await api.get(endpoint);
        data = response.data;
      }
      
      cacheRef.current.set(cacheKey, { data, timestamp: Date.now() });
      setState(prev => ({
        ...prev,
        cacheStatus: { ...prev.cacheStatus, processes: 'fresh' },
      }));
      setProcesses(data);
      
      return data;
    } catch (error: any) {
      console.warn(`❌ [DataConnection] Failed to fetch processes for ${environment}:`, error.message);
      const emptyData: any[] = [];
      cacheRef.current.set(cacheKey, { data: emptyData, timestamp: Date.now() });
      setState(prev => ({
        ...prev,
        cacheStatus: { ...prev.cacheStatus, processes: 'stale' },
      }));
      setProcesses(emptyData);
      return emptyData;
    }
  }, [environment]);

  const syncStates = useCallback(async () => {
    const cacheKey = `states_${environment}`;
    const cached = cacheRef.current.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setState(prev => ({
        ...prev,
        cacheStatus: { ...prev.cacheStatus, states: 'fresh' },
      }));
      setStates(cached.data);
      return cached.data;
    }
    
    try {
      // Live data: no global states API; use empty.
      const data: any[] = [];
      cacheRef.current.set(cacheKey, { data, timestamp: Date.now() });
      setState(prev => ({
        ...prev,
        cacheStatus: { ...prev.cacheStatus, states: 'stale' },
      }));
      setStates(data);
      return data;
    } catch (error) {
      setState(prev => ({
        ...prev,
        cacheStatus: { ...prev.cacheStatus, states: 'stale' },
      }));
      throw error;
    }
  }, [environment]);

  const syncTransitions = useCallback(async () => {
    const cacheKey = `transitions_${environment}`;
    const cached = cacheRef.current.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setState(prev => ({
        ...prev,
        cacheStatus: { ...prev.cacheStatus, transitions: 'fresh' },
      }));
      setTransitions(cached.data);
      return cached.data;
    }
    
    try {
      // Live data: no global transitions API; use empty.
      const data: any[] = [];
      cacheRef.current.set(cacheKey, { data, timestamp: Date.now() });
      setState(prev => ({
        ...prev,
        cacheStatus: { ...prev.cacheStatus, transitions: 'stale' },
      }));
      setTransitions(data);
      return data;
    } catch (error) {
      setState(prev => ({
        ...prev,
        cacheStatus: { ...prev.cacheStatus, transitions: 'stale' },
      }));
      throw error;
    }
  }, [environment]);

  const invalidateCache = useCallback((type?: keyof DataConnectionState['cacheStatus']) => {
    if (type) {
      setState(prev => ({
        ...prev,
        cacheStatus: { ...prev.cacheStatus, [type]: 'missing' },
      }));
      
      // Remove from cache
      const cacheKey = `${type}_${environment}`;
      cacheRef.current.delete(cacheKey);
    } else {
      // Invalidate all cache
      setState(prev => ({
        ...prev,
        cacheStatus: {
          artifacts: 'missing',
          processes: 'missing',
          states: 'missing',
          transitions: 'missing',
        },
      }));
      cacheRef.current.clear();
    }
  }, [environment]);

  const clearCache = useCallback(() => {
    cacheRef.current.clear();
    setState(prev => ({
      ...prev,
      cacheStatus: {
        artifacts: 'missing',
        processes: 'missing',
        states: 'missing',
        transitions: 'missing',
      },
    }));
  }, []);

  const getCacheStatus = useCallback(() => {
    return state.cacheStatus;
  }, [state.cacheStatus]);

  const fetchArtifacts = useCallback(async (_env: string) => {
    if (dataSource === 'sample') {
      return [];
    }

    return await syncArtifacts();
  }, [dataSource, syncArtifacts]);

  const fetchProcesses = useCallback(async (_env: string) => {
    if (dataSource === 'sample') {
      return [];
    }

    return await syncProcesses();
  }, [dataSource, syncProcesses]);

  const fetchStates = useCallback(async (_env: string) => {
    if (dataSource === 'sample') {
      return [];
    }

    return await syncStates();
  }, [dataSource, syncStates]);

  const fetchTransitions = useCallback(async (_env: string) => {
    if (dataSource === 'sample') {
      return [];
    }

    return await syncTransitions();
  }, [dataSource, syncTransitions]);

  const subscribeToUpdates = useCallback(() => {
    if (wsRef.current) return;
    
    const wsUrl = `${WS_BASE_URL}/ws/data`;
    wsRef.current = new WebSocket(wsUrl);
    
    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      // Handle real-time data updates
      if (data.type === 'artifact_updated') {
        invalidateCache('artifacts');
      } else if (data.type === 'process_updated') {
        invalidateCache('processes');
      } else if (data.type === 'state_updated') {
        invalidateCache('states');
      } else if (data.type === 'transition_updated') {
        invalidateCache('transitions');
      }
    };
    
    wsRef.current.onclose = () => {
      wsRef.current = null;
      // Attempt to reconnect after 5 seconds
      setTimeout(() => {
        if (state.isConnected) {
          subscribeToUpdates();
        }
      }, 5000);
    };
  }, [state.isConnected, invalidateCache]);

  const unsubscribeFromUpdates = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const getDataMetrics = useCallback(async (): Promise<DataMetrics> => {
    try {
      const response = await api.get('/data/metrics');
      return response.data;
    } catch (error) {
      // No mock fallback: return empty metrics when API fails
      return {
        totalArtifacts: artifacts.length,
        totalProcesses: processes.length,
        totalStates: states.length,
        totalTransitions: transitions.length,
        lastUpdated: new Date().toISOString(),
        dataSize: 0,
      };
    }
  }, [artifacts.length, processes.length, states.length, transitions.length]);

  const getConnectionHealth = useCallback(async () => {
    const startTime = Date.now();
    try {
      await api.get('/health/health');
      const latency = Date.now() - startTime;
      
      return {
        status: 'healthy',
        latency,
        errors: 0,
      };
    } catch (error: any) {
      console.warn('Health check failed:', error.message);
      return {
        status: 'unhealthy',
        latency: Date.now() - startTime,
        errors: 1,
      };
    }
  }, []);

  const handleConnectionError = useCallback((error: Error) => {
    console.error('Data connection error:', error);
    
    setState(prev => ({
      ...prev,
      isConnecting: false,
      isConnected: false,
      connectionError: error.message,
      retryCount: prev.retryCount + 1,
    }));
    
    // Auto-retry if under max retries
    if (state.retryCount < state.maxRetries) {
      setTimeout(() => {
        void reconnect();
      }, 2000 * (state.retryCount + 1)); // Exponential backoff
    } else {
      console.warn('Max retry attempts reached. Stopping connection attempts.');
    }
  }, [state.retryCount, state.maxRetries, reconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unsubscribeFromUpdates();
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [unsubscribeFromUpdates]);

  // Memoize context value to prevent unnecessary re-renders
  const value: DataConnectionContextType = useMemo(() => ({
    ...state,
    // Data properties
    artifacts,
    processes,
    states,
    transitions,
    isLoading,
    error,
    // Connection management
    connect,
    disconnect,
    reconnect,
    // Data synchronization
    syncData,
    syncArtifacts,
    syncProcesses,
    syncStates,
    syncTransitions,
    // Cache management
    invalidateCache,
    clearCache,
    getCacheStatus,
    // Data fetching
    fetchArtifacts,
    fetchProcesses,
    fetchStates,
    fetchTransitions,
    // WebSocket management
    subscribeToUpdates,
    unsubscribeFromUpdates,
    // Utilities
    getDataMetrics,
    getConnectionHealth,
    handleConnectionError,
    resetErrorState,
  }), [
    state,
    artifacts,
    processes,
    states,
    transitions,
    isLoading,
    error,
    connect,
    disconnect,
    reconnect,
    syncData,
    syncArtifacts,
    syncProcesses,
    syncStates,
    syncTransitions,
    invalidateCache,
    clearCache,
    getCacheStatus,
    fetchArtifacts,
    fetchProcesses,
    fetchStates,
    fetchTransitions,
    subscribeToUpdates,
    unsubscribeFromUpdates,
    getDataMetrics,
    getConnectionHealth,
    handleConnectionError,
    resetErrorState,
  ]);

  return (
    <DataConnectionContext.Provider value={value}>
      {children}
    </DataConnectionContext.Provider>
  );
};

export const useDataConnection = (): DataConnectionContextType => {
  const context = useContext(DataConnectionContext);
  if (context === undefined) {
    throw new Error('useDataConnection must be used within a DataConnectionProvider');
  }
  return context;
};
